<template>
  <!-- Phase 2 - US-014: NOTTO Dashboard -->
  <div class="mt-3">
    <div class="d-flex align-items-center mb-3">
      <h3>NOTTO Dashboard</h3>
      <div class="flex-grow-1"></div>
      <b-button
        :variant="autoRefresh ? 'success' : 'outline-secondary'"
        class="me-2"
        @click="toggleAutoRefresh"
      >
        <i class="mdi" :class="autoRefresh ? 'mdi-refresh' : 'mdi-refresh-auto'"></i>
        {{ autoRefresh ? "Auto-Refresh ON" : "Auto-Refresh OFF" }}
      </b-button>
      <b-button @click="$router.push('/')">Back</b-button>
    </div>
    <b-card class="mb-3">
      <b-row>
        <b-col cols="12" md="3">
          <date-picker
            v-model="filter.Fdate"
            label="From Date"
          ></date-picker>
        </b-col>
        <b-col cols="12" md="3">
          <date-picker
            v-model="filter.Edate"
            label="To Date"
          ></date-picker>
        </b-col>
        <b-col cols="12" md="2">
          <select-field
            v-model="filter.organ_id"
            label="Organ"
            :items="organList"
          ></select-field>
        </b-col>
        <b-col cols="12" md="2">
          <select-field
            v-model="filter.transplant_type"
            label="Transplant Type"
            :items="transplantTypes"
          ></select-field>
        </b-col>
        <b-col cols="12" md="2" class="d-flex align-items-end">
          <b-button variant="primary" class="me-2" @click="fetch"
            >Search</b-button
          >
          <b-button variant="outline-secondary" @click="reset">Reset</b-button>
        </b-col>
      </b-row>
    </b-card>
    <!-- Summary Cards -->
    <b-row class="mb-3" v-if="summary.total_transplants != null">
      <b-col cols="12" md="3">
        <b-card class="text-center">
          <h4>{{ summary.total_transplants }}</h4>
          <small class="text-muted">Total Transplants</small>
        </b-card>
      </b-col>
      <b-col cols="12" md="3">
        <b-card class="text-center">
          <h4>{{ summary.live_donor_count }}</h4>
          <small class="text-muted">Live Donor</small>
        </b-card>
      </b-col>
      <b-col cols="12" md="3">
        <b-card class="text-center">
          <h4>{{ summary.cadaver_count }}</h4>
          <small class="text-muted">Cadaver / Deceased</small>
        </b-card>
      </b-col>
      <b-col cols="12" md="3">
        <b-card class="text-center">
          <h4>{{ summary.swap_count }}</h4>
          <small class="text-muted">Swap Transplant</small>
        </b-card>
      </b-col>
    </b-row>
    <!-- NOTTO Transplant Table -->
    <table-view
      title="NOTTO Transplant Records"
      :fields="fields"
      :isBusy="isLoading"
      :items="list"
    >
      <template v-slot:transplant_type_name="{ value }">
        <span :class="getTypeClass(value)">{{ value }}</span>
      </template>
      <template v-slot:crossmatch_result="{ value }">
        <span :class="getCrossmatchClass(value)">{{ formatCrossmatch(value) }}</span>
      </template>
    </table-view>
  </div>
</template>

<script>
import { formatDate } from "../assets/js/common";
import liveTransplantService from "../assets/services/live.transplant.service";
import TableView from "../components/table/TableView.vue";
import DatePicker from "../components/form/DatePicker.vue";
import SelectField from "../components/form/SelectField.vue";
import {
  TRANSPLANT_TYPES,
  CROSSMATCH_RESULTS,
} from "../assets/constants/notto.constants";
export default {
  components: { TableView, DatePicker, SelectField },
  data() {
    return {
      isLoading: false,
      autoRefresh: false,
      refreshInterval: null,
      filter: {
        Fdate: "",
        Edate: "",
        organ_id: "",
        transplant_type: "",
      },
      list: [],
      summary: {},
      organList: [],
      transplantTypes: TRANSPLANT_TYPES,
    };
  },
  computed: {
    fields() {
      return [
        { key: "sno", label: "S.No" },
        { key: "notto_id", label: "NOTTO ID" },
        { key: "donor_name", label: "Donor Name" },
        { key: "recipient_name", label: "Recipient Name" },
        { key: "organ_name", label: "Organ" },
        { key: "transplant_type_name", label: "Transplant Type" },
        { key: "crossmatch_result", label: "Crossmatch" },
        { key: "hla_typing", label: "HLA Typing" },
        { key: "immunosuppression_protocol", label: "Immunosuppression" },
        { key: "date_of_surgery", label: "Date of Surgery" },
        { key: "hospital_name", label: "Hospital" },
      ];
    },
  },
  methods: {
    formatCrossmatch(value) {
      let item = CROSSMATCH_RESULTS.find((s) => s.value == value);
      return item ? item.name : value || "";
    },
    getCrossmatchClass(value) {
      switch (value) {
        case "negative":
          return "badge bg-success font-size-13";
        case "positive":
          return "badge bg-danger font-size-13";
        default:
          return "badge bg-secondary font-size-13";
      }
    },
    getTypeClass(value) {
      switch (value) {
        case "Live Donor":
          return "badge bg-info font-size-13";
        case "Cadaver / Deceased":
          return "badge bg-warning font-size-13";
        case "Swap":
          return "badge bg-primary font-size-13";
        default:
          return "";
      }
    },
    toggleAutoRefresh() {
      this.autoRefresh = !this.autoRefresh;
      if (this.autoRefresh) {
        this.refreshInterval = setInterval(() => {
          this.fetch();
        }, 30000);
      } else {
        clearInterval(this.refreshInterval);
        this.refreshInterval = null;
      }
    },
    reset() {
      this.filter = {
        Fdate: "",
        Edate: "",
        organ_id: "",
        transplant_type: "",
      };
      this.list = [];
      this.summary = {};
    },
    async fetch() {
      const vm = this;
      try {
        vm.isLoading = true;
        vm.list = [];
        let doc = {
          dml_indicator: "ND",
          ...vm.filter,
        };
        let { data } = await liveTransplantService.nottoDashboard(doc);
        let { status, msg, list } = data;
        vm.isLoading = false;
        if (!status) {
          vm.$alert.show(msg);
          return;
        }
        if (data.summary) {
          let summaryData = data.summary;
          vm.summary =
            summaryData instanceof Array ? summaryData[0] || {} : summaryData;
        }
        vm.list = (list || []).map((item, i) => {
          item.sno = i + 1;
          item.date_of_surgery = formatDate(item.date_of_surgery);
          return item;
        });
      } catch (error) {
        vm.isLoading = false;
        vm.$alert.show(error.message);
      }
    },
    async fetchOrgans() {
      const vm = this;
      try {
        let organs = vm.$store.getters.master.master_organ;
        if (organs instanceof Array) {
          vm.organList = organs.map((item) => ({
            name: item.organ_name,
            value: item.organ_id,
          }));
        }
      } catch (error) {
        console.error(error);
      }
    },
  },
  mounted() {
    this.fetchOrgans();
  },
  beforeDestroy() {
    if (this.refreshInterval) {
      clearInterval(this.refreshInterval);
    }
  },
};
</script>
